import type {PropsWithChildren, ReactElement} from 'react'
import React, {Children, cloneElement} from 'react'
import {ButtonWithDropdown} from '../common/ButtonWithDropdown'
import {Button} from '@primer/react'
import {useIsMergeBoxSidePanel} from '../../../contexts/MergeBoxLayoutContext'

/**
 *
 * Formats the actions in the merge section
 * Standardizes the space between different slots
 * At small viewport sizes and below, wraps the second slot beneath the first slot and makes any buttons fill the full available width
 */
export function MergeSectionActions({
  children,
  className,
  inline,
}: PropsWithChildren<{className?: string; inline?: boolean}>) {
  const isSidePanel = useIsMergeBoxSidePanel()
  const defaultClassNames =
    isSidePanel && !inline ? 'flex-sm-column flex-items-start gap-3' : 'flex-sm-row flex-sm-items-center gap-2'
  return <div className={`d-flex flex-column ${defaultClassNames} ${className}`}>{children}</div>
}

/**
 * The child can be any single valid React element
 * For Button or ButtonWithDropdown elements, it will apply the correct class to ensure the button fills the full width at small viewport sizes and below
 */
MergeSectionActions.Slot = function MergeSectionsActionsSlot({children}: PropsWithChildren) {
  const isSidePanel = useIsMergeBoxSidePanel()
  // eslint-disable-next-line @eslint-react/no-children-only
  let element = Children.only(children)

  if (React.isValidElement(element)) {
    element = applyStretchStyleToChild(element, isSidePanel)
  }

  return element
}

function applyStretchStyleToChild(element: ReactElement, isSidePanel: boolean) {
  if (element.type === ButtonWithDropdown || element.type === Button) {
    const child = element as React.ReactElement<
      React.ComponentProps<typeof ButtonWithDropdown> | React.ComponentProps<typeof Button>
    >
    const stretchClass = isSidePanel ? 'flex-shrink-0' : 'flex-self-stretch flex-shrink-0'
    // eslint-disable-next-line @eslint-react/no-clone-element
    return cloneElement(child, {
      className: `${stretchClass} ${child.props.className}`,
    })
  }
  return element
}
